import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

export default function BoylesLawCalculator() {
  const [p1, setP1] = useState("");
  const [v1, setV1] = useState("");
  const [p2, setP2] = useState("");
  const [v2, setV2] = useState("");

  const calculateMissing = () => {
    const values = [p1, v1, p2, v2];
    const emptyCount = values.filter(v => !v).length;

    if (emptyCount !== 1) {
      toast({
        title: "Invalid Input",
        description: "Please provide exactly 3 values to calculate the 4th.",
        variant: "destructive",
      });
      return;
    }

    const P1 = parseFloat(p1);
    const V1 = parseFloat(v1);
    const P2 = parseFloat(p2);
    const V2 = parseFloat(v2);

    // P1*V1 = P2*V2
    if (!p1 && V1 && P2 && V2) {
      const result = (P2 * V2) / V1;
      setP1(result.toFixed(4));
      toast({ title: "P₁ Calculated", description: `P₁ = ${result.toFixed(4)} atm` });
    } else if (!v1 && P1 && P2 && V2) {
      const result = (P2 * V2) / P1;
      setV1(result.toFixed(4));
      toast({ title: "V₁ Calculated", description: `V₁ = ${result.toFixed(4)} L` });
    } else if (!p2 && P1 && V1 && V2) {
      const result = (P1 * V1) / V2;
      setP2(result.toFixed(4));
      toast({ title: "P₂ Calculated", description: `P₂ = ${result.toFixed(4)} atm` });
    } else if (!v2 && P1 && V1 && P2) {
      const result = (P1 * V1) / P2;
      setV2(result.toFixed(4));
      toast({ title: "V₂ Calculated", description: `V₂ = ${result.toFixed(4)} L` });
    }
  };

  const reset = () => {
    setP1("");
    setV1("");
    setP2("");
    setV2("");
  };

  return (
    <PageLayout 
      calculatorName="Boyle's Law Calculator" 
      calculatorPath="/calculator/boyles-law"
      category="chemistry"
      subcategory="Gases"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Boyle's Law Calculator</CardTitle>
            <CardDescription>
              P₁V₁ = P₂V₂ (Temperature constant)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="p1">Initial Pressure P₁ (atm)</Label>
                  <Input
                    id="p1"
                    type="number"
                    step="any"
                    value={p1}
                    onChange={(e) => setP1(e.target.value)}
                    placeholder="Leave blank to solve"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="v1">Initial Volume V₁ (L)</Label>
                  <Input
                    id="v1"
                    type="number"
                    step="any"
                    value={v1}
                    onChange={(e) => setV1(e.target.value)}
                    placeholder="Leave blank to solve"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="p2">Final Pressure P₂ (atm)</Label>
                  <Input
                    id="p2"
                    type="number"
                    step="any"
                    value={p2}
                    onChange={(e) => setP2(e.target.value)}
                    placeholder="Leave blank to solve"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="v2">Final Volume V₂ (L)</Label>
                  <Input
                    id="v2"
                    type="number"
                    step="any"
                    value={v2}
                    onChange={(e) => setV2(e.target.value)}
                    placeholder="Leave blank to solve"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculateMissing} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {(p1 || v1 || p2 || v2) && (
              <CalculatorSuperCalcCTA config={calculatorCTAs["boyles-law"]} trigger={p1 + v1 + p2 + v2} />
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Boyle's Law</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">P₁V₁ = P₂V₂</p>
                <p className="text-sm text-muted-foreground mt-2">
                  At constant temperature, pressure and volume are inversely proportional
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
